import { DigitalWalletManagementFilterRequest } from "@/models/digital.wallet";
import { Box, Collapse, FormControl, Grid, MenuItem, IconButton } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputFilterFieldStyled from "../styled/input.filter.field.styled";
import InputLabelStyled from "../styled/input.label.styled";
import DigitalWalletStatusSelect from "../common/digital.wallet.status.select";
import BlueButtonStyled from "../styled/blue.button.styled";
import DatePickerComponent from "../common/date.picker.component";
import StatusSelect from "../common/status.select";
import { Moment } from "moment";
import filtersStore from "@/stores/filtersStore";
import { EmployeeGroup, OrganizationUnit } from "@/models/apiModels";
import BaseInput from "../common/baseInput";
import api from "@/services/api";
import ClearIcon from "@/assets/icons/clear-icon.svg";

interface IDigitalWalletManagementFilter {
    searchClick: Function;
    filter: DigitalWalletManagementFilterRequest;
    setFilter: Function;
}

const DigitalWalletManagementFilter: FC<IDigitalWalletManagementFilter> = ({
    searchClick,
    filter,
    setFilter,
}: IDigitalWalletManagementFilter) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(true);
    const [organizationUnits, setOrganizationUnits] = useState<OrganizationUnit[]>([]);
    const [employeeGroups, setEmployeeGroups] = useState<EmployeeGroup[]>([]);
    const onKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            searchClick();
        }
    };

    const setHiddenFilter = () => {
        setOpen(!open);
        filtersStore.setDigitalWalletManagmentHiddenFilter(!open);
    };

    function clearFilter() {
        filtersStore.setDigitalWalletManagmentHiddenFilter(null);
        setFilter(() => ({
            ...new DigitalWalletManagementFilterRequest(),
        }));
        searchClick();
    }

    function fetchHiddenFilter() {
        if (filtersStore.digitalWalletManagmentHiddenFilter != null) {
            setOpen(filtersStore.digitalWalletManagmentHiddenFilter);
        }
    }
    async function getEmployeesInitData() {
        const employeesListDataResponsePromise = api.getEmployeesPageData();
        const employeesListDataResponse = await employeesListDataResponsePromise;
        setOrganizationUnits(employeesListDataResponse.data.organizationUnits);
        setEmployeeGroups(employeesListDataResponse.data.employeeGroups);
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            fetchHiddenFilter();
            getEmployeesInitData();
        }
        return () => {
            ignore = true;
        };
    }, []);

    return (
        <Grid>
            <Grid
                sx={{
                    background: "#FFFFFF",
                    border: "1px solid rgba(64, 82, 137, 0.25)",
                    borderRadius: "12px",
                    padding: "15px",
                    width: "100%",
                    margin: "20px 0",
                }}
            >
                <Grid sx={{ marginBottom: "5px" }}>
                    <InputLabelStyled
                        shrink
                        disableAnimation
                        size="normal"
                        htmlFor="searchFilter-freetext"
                        className="free-text-label"
                    >
                        {t("general.SearchFilterHeader")}
                    </InputLabelStyled>
                </Grid>
                <Grid
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "15px",
                    }}
                >
                    <FormControl variant="standard" sx={{ width: "20%" }}>
                        <InputFilterFieldStyled
                            label={t("general.SearchFilterEmployeesName")}
                            type="search"
                            size="small"
                            value={filter.employeeName}
                            onKeyDown={onKeyPressHandler}
                            onChange={(event) =>
                                setFilter(() => ({
                                    ...filter,
                                    employeeName: event.target.value,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl variant="standard" sx={{ width: "20%" }}>
                        <DatePickerComponent
                            title={t("orders.fromDate")}
                            value={filter.fromDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    fromDate: args,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl variant="standard" sx={{ width: "20%" }}>
                        <DatePickerComponent
                            title={t("orders.endDate")}
                            value={filter.tillDate}
                            minDate={filter.fromDate}
                            updateDate={(args: Moment | null) =>
                                setFilter(() => ({
                                    ...filter,
                                    tillDate: args,
                                }))
                            }
                        />
                    </FormControl>
                    <FormControl size="small" sx={{ width: "20%" }}>
                        <DigitalWalletStatusSelect
                            value={filter.walletStatus}
                            title={t("general.SearchFilterWalletStatus")}
                            updateStatus={(args?: string | null) => {
                                setFilter(() => ({
                                    ...filter,
                                    walletStatus: args,
                                }));
                            }}
                        ></DigitalWalletStatusSelect>
                    </FormControl>
                    <Grid sx={{ width: "6%" }}></Grid>
                    <Grid
                        sx={{
                            display: "flex",
                            gap: "20px",
                        }}
                    >
                        <FormControl
                            sx={{
                                display: "flex",
                                flexDirection: "column-reverse",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "100%",
                                }}
                                className="link-element clear-all-btn"
                                onClick={() => {
                                    clearFilter();
                                }}
                            >
                                {t("display.ClearAll")}
                            </Box>
                        </FormControl>
                        <FormControl>
                            <BlueButtonStyled onClick={() => searchClick()} sx={{ width: "100%", maxWidth: "110px" }}>
                                {t("general.filter")}
                            </BlueButtonStyled>
                        </FormControl>
                    </Grid>
                </Grid>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Grid
                        sx={{
                            display: "flex",
                            gap: "15px",
                            padding: "20px 0px 0",
                        }}
                    >
                        <FormControl size="small" sx={{ width: "15%" }}>
                            <StatusSelect
                                value={filter.employeeStatus}
                                title={t("general.SearchFilterEmployeesStatus")}
                                updateStatus={(args: boolean | null) =>
                                    setFilter(() => ({
                                        ...filter,
                                        employeeStatus: args,
                                    }))
                                }
                            ></StatusSelect>
                        </FormControl>
                        <FormControl size="small" sx={{ width: "15%" }}>
                            <BaseInput
                                select
                                label={t("employee.EmployeeGroup")}
                                size="small"
                                value={filter?.employeeGroupId ?? ""}
                                onChange={(event: any) =>
                                    setFilter(() => ({
                                        ...filter,
                                        employeeGroupId: event.target.value,
                                    }))
                                }
                                sx={{
                                    fontSize: 14,
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            className="select-clear-icon"
                                            sx={{
                                                visibility: filter.employeeGroupId ? "visible" : "hidden",
                                            }}
                                            onClick={() =>
                                                setFilter(() => ({
                                                    ...filter,
                                                    employeeGroupId: null,
                                                }))
                                            }
                                        >
                                            <img src={ClearIcon} alt="Clear icon" />
                                        </IconButton>
                                    ),
                                }}
                            >
                                {employeeGroups.map((employeeGroup) => (
                                    <MenuItem key={employeeGroup.id} value={employeeGroup.id}>
                                        {employeeGroup.text}
                                    </MenuItem>
                                ))}
                            </BaseInput>
                        </FormControl>
                        <FormControl size="small" sx={{ width: "15%" }}>
                            <BaseInput
                                select
                                id="department-input"
                                label={t("employee.Department")}
                                size="small"
                                value={filter?.organizationUnitId ?? ""}
                                onChange={(event: any) =>
                                    setFilter(() => ({
                                        ...filter,
                                        organizationUnitId: event.target.value,
                                    }))
                                }
                                InputProps={{
                                    endAdornment: (
                                        <IconButton
                                            className="select-clear-icon"
                                            sx={{
                                                visibility: filter.organizationUnitId ? "visible" : "hidden",
                                            }}
                                            onClick={() =>
                                                setFilter(() => ({
                                                    ...filter,
                                                    organizationUnitId: null,
                                                }))
                                            }
                                        >
                                            <img src={ClearIcon} alt="Clear icon" />
                                        </IconButton>
                                    ),
                                }}
                            >
                                {organizationUnits.map((organizationUnit) => (
                                    <MenuItem key={organizationUnit.id} value={organizationUnit.id}>
                                        {organizationUnit.text}
                                    </MenuItem>
                                ))}
                            </BaseInput>
                        </FormControl>
                    </Grid>
                </Collapse>
            </Grid>
        </Grid>
    );
};

export default DigitalWalletManagementFilter;
